/**
 * ศูนย์สมาชิกบนหน้า "ออร์เดอร์ของฉัน" — ป้ายประกาศ + ถุงทองกิจกรรม (logic ล้วน · node --test)
 * ข้อมูลมาจาก memberApi.listMyOrders (admin repo: src/member/memberApi.js · memberAnnouncements.js · memberUiState.js)
 * ตัดสินการปิดป้ายฝั่ง server เสมอ — client ส่งแค่ว่าเห็นสไลด์ไหน + ติ๊ก "ไม่ต้องแสดงอีกวันนี้" สไลด์ไหน
 */

export type CoverPresetId = 'shipping' | 'ceremony' | 'welcome' | 'promo' | 'gold' | 'cloth'
export interface CoverValue { preset: CoverPresetId | ''; imageUrl: string }

export type DismissMode = 'optout' | 'daily' | 'forever'
export type AnnouncementButtonKind = 'orders' | 'line' | 'link' | 'close'

export interface AnnouncementButton { label: string; kind: AnnouncementButtonKind; value: string }

export interface MemberAnnouncement {
  id: string
  version: number
  title: string
  body: string
  cover?: CoverValue
  buttons: AnnouncementButton[]
  dismissMode: DismissMode
}

export interface HubActivity {
  id: string
  version?: number
  name: string
  description: string
  keywords: string[]
  startAt: number | null
  endAt: number | null
  prizeRemaining?: number
  prizeTotal?: number
  cover?: CoverValue
  distribution?: 'fcfs' | 'random'
  isNew?: boolean
}

export const COVER_PRESETS: Record<CoverPresetId, { bg: string; icon: string; tag: string }> = {
  shipping: { bg: 'linear-gradient(135deg,#0f3d4a,#1d2a52 55%,#3b1d4f)', icon: 'fa-truck-fast', tag: 'ประกาศ' },
  ceremony: { bg: 'radial-gradient(circle at 50% 120%,rgba(240,171,252,.45),transparent 60%),linear-gradient(135deg,#3b0f74,#7b1fa2 55%,#b45309)', icon: 'fa-om', tag: 'พิธี' },
  welcome: { bg: 'linear-gradient(135deg,#4a2f08,#a47b1e 55%,#e8c46a)', icon: 'fa-crown', tag: 'สมาชิก' },
  promo: { bg: 'linear-gradient(135deg,#7f1d1d,#be123c 50%,#f59e0b)', icon: 'fa-gift', tag: 'โปร' },
  gold: { bg: 'radial-gradient(circle at 50% 40%,rgba(255,236,170,.55),transparent 55%),linear-gradient(135deg,#5b3a07,#b8862a 60%,#f5d27a)', icon: 'fa-coins', tag: 'ลุ้นรางวัล' },
  cloth: { bg: 'linear-gradient(135deg,#6b1010,#b91c1c 55%,#eab308)', icon: 'fa-scroll', tag: 'แจกฟรี' },
}

export function coverOf(cover: CoverValue | undefined): { image: string; bg: string; icon: string; tag: string } | null {
  if (!cover) return null
  if (cover.imageUrl && /^https:\/\//i.test(cover.imageUrl)) return { image: cover.imageUrl, bg: '', icon: '', tag: '' }
  const preset = cover.preset ? COVER_PRESETS[cover.preset] : undefined
  return preset ? { image: '', ...preset } : null
}

/** เก็บเฉพาะข้อมูลที่ใช้ได้ (API เวอร์ชันเก่าไม่มี announcements = ไม่มีป้าย) */
export function readAnnouncements(raw: unknown): MemberAnnouncement[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((a): a is MemberAnnouncement => !!a && typeof a === 'object' && typeof (a as MemberAnnouncement).id === 'string' && !!(a as MemberAnnouncement).title)
    .map((a) => ({
      ...a,
      version: Number(a.version) || 1,
      buttons: Array.isArray(a.buttons) ? a.buttons.filter((b) => b && b.label).slice(0, 2) : [],
      dismissMode: (['optout', 'daily', 'forever'] as const).includes(a.dismissMode) ? a.dismissMode : 'optout',
    }))
    .slice(0, 8)
}

export function readActivities(raw: unknown): HubActivity[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((a): a is HubActivity => !!a && typeof a === 'object' && typeof (a as HubActivity).id === 'string')
}

/** ประกาศรุ่นนี้ (id+version) — แก้เนื้อหาแล้ว version เพิ่ม = นับเป็นป้ายใหม่ */
export function announcementKey(a: MemberAnnouncement): string {
  return `${a.id}:${Number(a.version) || 1}`
}

/**
 * ป้ายที่ยังไม่เคยขึ้นในการเปิดหน้านี้ — ข้อมูลรีเฟรชระหว่างเปิดหน้า (กดถุงทอง/กลับมาที่แท็บ) ขึ้นเฉพาะป้ายใหม่
 * ป้ายที่ลูกค้าเพิ่งปิดแบบไม่ติ๊ก "ไม่ต้องแสดงอีกวันนี้" จะไม่เด้งซ้ำจนกว่าจะเปิดหน้าใหม่ (พฤติกรรมเดิม)
 */
export function unshownAnnouncements(list: MemberAnnouncement[], shown: ReadonlySet<string>): MemberAnnouncement[] {
  return list.filter((a) => !shown.has(announcementKey(a)))
}

/** ข้อมูลที่ส่งตอนปิดป้าย — เฉพาะสไลด์ที่ลูกค้าเห็นจริง */
export function buildCloseEvent(deck: MemberAnnouncement[], seenIds: Set<string>, optOutIds: Set<string>) {
  const seen = deck.filter((a) => seenIds.has(a.id)).map((a) => ({ id: a.id, version: a.version }))
  const optOut = deck.filter((a) => seenIds.has(a.id) && a.dismissMode === 'optout' && optOutIds.has(a.id)).map((a) => a.id)
  return { seen, optOut }
}

/** ลิงก์เปิดแชท LINE พร้อมพิมพ์ข้อความให้ (ใช้ได้ทั้งใน LIFF และเบราว์เซอร์) */
export function lineOaMessageUrl(lineId: string, text: string): string {
  return `https://line.me/R/oaMessage/${encodeURIComponent(lineId)}/?${encodeURIComponent(text)}`
}

const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

export function endLabel(endAt: number | null): string {
  if (!endAt) return 'ไม่มีกำหนดหมดเขต'
  const d = new Date(endAt + 7 * 3600_000)
  return `ถึง ${d.getUTCDate()} ${TH_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear() + 543}`
}

export function prizeInfo(a: HubActivity): { pct: number; low: boolean; text: string } | null {
  const total = Number(a.prizeTotal) || 0
  if (total <= 0) return null
  const left = Math.max(0, Number(a.prizeRemaining) || 0)
  return { pct: Math.round((left / total) * 100), low: left / total <= 0.25, text: `${left}/${total}` }
}

/**
 * ลำดับหลัง login: ป้ายประกาศ (ถ้ามี) → ปุ่มถุงทองลอยที่มุมจอ
 * กิจกรรมใหม่ = กล่องของขวัญลอยเกาะข้างถุงทอง (ไม่มีป๊อปอัพบังหน้า) — ลูกค้าแตะเองเมื่อพร้อม → 'gift' (เปิดกล่อง กลางจอ)
 * 'gift' เกิดจากการแตะเท่านั้น ไม่เคยเป็นสถานะเริ่มต้น
 */
export type HubStage = 'announcements' | 'gift' | 'fab' | 'none'

export function initialStage(announcements: MemberAnnouncement[]): HubStage {
  return announcements.length ? 'announcements' : 'fab'
}

export function afterAnnouncements(): HubStage {
  return 'fab'
}

/** กล่องของขวัญที่มุมจอ: มีกิจกรรมที่ยังไม่เคยเห็น + ลูกค้ามีสิทธิ์ร่วมกิจกรรม */
export function giftWaiting(hasNew: boolean, seenNew: boolean, benefitsEligible: boolean): boolean {
  return benefitsEligible && hasNew && !seenNew
}
